// Event packages — whole-court bookings sold as a block of hours rather than
// by the hour (birthdays, corporate nights, tournaments). Authored in
// Admin -> Events and Packages; the shape lives in ConfigContext's
// DEFAULT_CONFIG.
//
// Everything here is pure config math with no Firebase dependency, so the
// admin screen, its validation and (later) the guest booking flow all agree
// on what a package's window and prices actually mean.

import { bandAppliesOnDay, isHourInBand, parseTimeSlotStartHour, getAllConfiguredTimeSlots, WEEKDAYS } from './bookingUtils';
import { formatMoney } from './currencyUtil';

/**
 * Marker written onto a booking record created from a package, so the
 * hourly grid can render the court as BOOKED while everything downstream
 * (admin list, export, receipts) can still tell an event apart from an
 * ordinary hourly reservation.
 *
 * Defined here — not at the call site — so the authoring screen and the
 * booking flow that follows it can never drift onto different spellings.
 */
export const BOOKING_SOURCE_PACKAGE = 'package';

/** @returns {Array} configured packages, always an array. */
export const getPackages = (config) => (Array.isArray(config?.packages) ? config.packages : []);

/** Packages a guest should actually see — `active: false` hides one without deleting it. */
export const getActivePackages = (config) => getPackages(config).filter(p => p?.active !== false);

/** @returns {object|null} a package by id. */
export const getPackageById = (config, packageId) =>
  getPackages(config).find(p => String(p.id) === String(packageId)) || null;

/**
 * How many hours wide a package's bookable window is.
 *
 * startHour/endHour are 0-23 and endHour <= startHour WRAPS past midnight,
 * the same convention rate bands use — so 3PM-12AM is stored as 15 -> 0 and
 * measures 9 hours. start === end means the package runs all day (24), not
 * zero, since a zero-width window could never be booked at all.
 *
 * @param {object} pkg
 * @returns {number} 1-24
 */
export const getPackageWindowHours = (pkg) => {
  const start = Number(pkg?.startHour) || 0;
  const end = Number(pkg?.endHour) || 0;
  const span = (end - start + 24) % 24;
  return span === 0 ? 24 : span;
};

/**
 * The last hour a package of `hours` length can START and still finish
 * inside the window.
 *
 * A 4-hour package in a 3PM-12AM window can start at 8PM at the latest
 * (20 = 15 + 9 - 4). Returns null when the package is longer than its own
 * window, which is a config error rather than a scheduling one — see
 * packageValidationUtil.
 *
 * The result is NOT wrapped back into 0-23: a window running 8PM-4AM
 * legitimately produces start hours past 24, and the caller needs to know
 * which calendar day it lands on. Use `% 24` at display time.
 *
 * @param {object} pkg
 * @param {number} hours
 * @returns {number|null}
 */
export const getLatestPackageStartHour = (pkg, hours) => {
  const duration = Number(hours) || 0;
  if (duration <= 0) return null;
  const windowHours = getPackageWindowHours(pkg);
  if (duration > windowHours) return null;
  return (Number(pkg?.startHour) || 0) + (windowHours - duration);
};

/**
 * Every hour a package of `hours` length could start at, as absolute hours
 * from the window's start day (so a wrapping window yields 24, 25, ... for
 * the small hours of the next morning).
 *
 * @param {object} pkg
 * @param {number} hours
 * @returns {number[]} empty when the package can't fit its own window
 */
export const getPackageStartHours = (pkg, hours) => {
  const latest = getLatestPackageStartHour(pkg, hours);
  if (latest === null) return [];
  const start = Number(pkg?.startHour) || 0;
  const out = [];
  for (let h = start; h <= latest; h += 1) out.push(h);
  return out;
};

/**
 * The cleanup/turnover gap that applies to a package, in minutes: its own
 * override when set, otherwise the club-wide default.
 *
 * A blank override is stored as null/'' and must fall through, but an
 * explicit 0 is a real choice ("no gap needed") and must NOT — hence the
 * null/'' check rather than a truthiness test.
 *
 * @param {object} config
 * @param {object} pkg
 * @returns {number} minutes, never negative
 */
export const getPackageBufferMinutes = (config, pkg) => {
  const own = pkg?.bufferMinutes;
  const raw = (own === null || own === undefined || own === '')
    ? config?.packageBufferMinutes
    : own;
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

/**
 * The rate rows on one package option.
 *
 * An option is ONE duration ("4 hours") that may be priced several ways —
 * `rates: [{ id, price, days }]` — so a club charging ₱1,500 on weekdays and
 * ₱2,000 on weekends describes one 4-hour option with two rates, not two
 * 4-hour options. `days: []` on a rate means every day, the same convention
 * a sport's rate bands use.
 *
 * Options saved before rates existed carried a single `price`/`days` pair on
 * the option itself; those are read back as a one-rate list so old configs
 * keep pricing identically until the admin next saves.
 *
 * @param {object} option
 * @returns {Array<{id: string, price: *, days: number[]}>}
 */
export const getOptionRates = (option) => {
  if (!option) return [];
  if (Array.isArray(option.rates)) return option.rates.filter(Boolean);
  return [{
    id: `${option.id || 'opt'}-rate`,
    price: option.price,
    days: Array.isArray(option.days) ? option.days : [],
  }];
};

/**
 * The rate on an option that applies to a weekday, or null when none does.
 * First match wins; validation flags rates that overlap rather than this
 * silently picking a side.
 *
 * @param {object} option
 * @param {number|null} weekday - 0 = Sunday, null = don't filter
 * @returns {object|null}
 */
export const getOptionRateForDay = (option, weekday = null) =>
  getOptionRates(option).find(rate => bandAppliesOnDay(rate, weekday)) || null;

/**
 * A package's options as ONE per duration, ascending, with the rates of any
 * same-duration options folded together.
 *
 * Merging happens on read, not only when the admin saves, because a config
 * authored before rates were nested still holds "4 hrs / ₱1,500 weekdays" and
 * "4 hrs / ₱2,000 weekends" as two separate options — and a guest offered two
 * rows both labelled "4-Hour Package" is being asked a question they have no
 * way to answer. Everything guest-facing reads through here so the merged and
 * unmerged shapes behave identically.
 *
 * Blank-duration options are dropped: they're an unfinished editor row, not
 * something bookable.
 *
 * @param {object} pkg
 * @returns {Array}
 */
export const getMergedPackageOptions = (pkg) => {
  const byHours = new Map();
  (Array.isArray(pkg?.options) ? pkg.options : []).forEach((option) => {
    if (!option) return;
    const hours = Number(option.hours) || 0;
    if (hours <= 0) return;
    const existing = byHours.get(hours);
    if (existing) existing.rates.push(...getOptionRates(option));
    // The first option of a duration keeps its id, so a choice already made
    // survives a re-read of the same config.
    else byHours.set(hours, { ...option, hours, rates: [...getOptionRates(option)] });
  });
  return [...byHours.values()]
    .map(({ price, days, ...option }) => option)
    .sort((a, b) => a.hours - b.hours);
};

/**
 * The durations available on a given weekday — a duration counts only when at
 * least one of its rates applies that day — cheapest first.
 *
 * @param {object} pkg
 * @param {number|null} weekday - 0 = Sunday, null = don't filter
 * @returns {Array}
 */
export const getPackageOptionsForDay = (pkg, weekday = null) =>
  getMergedPackageOptions(pkg).filter(opt => getOptionRateForDay(opt, weekday) !== null);

/**
 * What a given duration costs on a given weekday, or null when this package
 * doesn't offer that duration then.
 *
 * @returns {number|null}
 */
export const getPackageOptionPrice = (pkg, hours, weekday = null) => {
  const option = getPackageOptionsForDay(pkg, weekday)
    .find(opt => (Number(opt.hours) || 0) === (Number(hours) || 0));
  if (!option) return null;
  const rate = getOptionRateForDay(option, weekday);
  return rate ? (Number(rate.price) || 0) : null;
};

/**
 * Package options in the canonical one-option-per-duration shape: legacy flat
 * options are lifted into `rates`, and options that sell the same number of
 * hours are folded into a single option carrying all of their rates.
 *
 * Run when the admin editor loads a package, so a config authored under the
 * old shape (a separate card per weekday price) opens as one card per
 * duration instead of tripping the duplicate-duration error.
 *
 * @param {Array} options
 * @param {(prefix: string) => string} makeId - id factory for merged rates
 * @returns {Array}
 */
export const normalizePackageOptions = (options, makeId = (p) => `${p}-${Math.random().toString(36).slice(2, 9)}`) => {
  const byHours = new Map();
  (Array.isArray(options) ? options : []).forEach((option) => {
    if (!option) return;
    const hours = option.hours === '' || option.hours === null || option.hours === undefined
      ? ''
      : (Number(option.hours) || 0);
    const rates = getOptionRates(option).map(rate => ({
      id: rate.id || makeId('rate'),
      price: rate.price ?? '',
      days: Array.isArray(rate.days) ? rate.days.map(Number) : [],
    }));
    // Blank durations can't be merged — two half-filled rows are two distinct
    // options the admin is still typing, not one duration priced twice.
    const key = hours === '' ? `blank-${option.id || makeId('opt')}` : `h-${hours}`;
    const existing = byHours.get(key);
    if (existing) {
      existing.rates.push(...rates);
    } else {
      byHours.set(key, { ...option, id: option.id || makeId('opt'), hours, rates });
    }
  });
  // `price`/`days` are the legacy per-option fields; drop them so the saved
  // record has exactly one source of truth for pricing.
  return [...byHours.values()].map(({ price, days, ...option }) => option);
};

/** @returns {object|null} one merged option (all of its rates) by id. */
export const getPackageOptionById = (pkg, optionId) =>
  getMergedPackageOptions(pkg).find(o => String(o.id) === String(optionId)) || null;

/** True when the package sells preset durations, as opposed to hourly only. */
export const packageHasFixedOptions = (pkg) => getMergedPackageOptions(pkg).length > 0;

/**
 * The preset durations a guest picks between, ready to render as a list:
 * "4-Hour Package · ₱1,500 – ₱2,000".
 *
 * The price is given as a RANGE because the option is chosen before the date
 * is: a 4-hour package costing ₱1,500 on weekdays and ₱2,000 at the weekend
 * has no single answer yet, and quoting only the cheaper one would read as a
 * bait. Pass a weekday once one is known to collapse it to one number.
 *
 * @param {object} pkg
 * @param {number|null} weekday
 * @returns {Array<{id: string, option: object, hours: number, label: string, minPrice: number|null, maxPrice: number|null}>}
 */
export const getPackageOptionChoices = (pkg, weekday = null) =>
  getPackageOptionsForDay(pkg, weekday)
    .filter(opt => (Number(opt.hours) || 0) > 0)
    .map((opt) => {
      const rates = weekday === null
        ? getOptionRates(opt)
        : [getOptionRateForDay(opt, weekday)].filter(Boolean);
      const prices = rates
        .map(rate => rate?.price)
        .filter(price => price !== '' && price !== null && price !== undefined)
        .map(Number)
        .filter(Number.isFinite);
      const hours = Number(opt.hours) || 0;
      return {
        id: opt.id,
        option: opt,
        hours,
        label: `${hours}-Hour Package`,
        minPrice: prices.length > 0 ? Math.min(...prices) : null,
        maxPrice: prices.length > 0 ? Math.max(...prices) : null,
      };
    });

/**
 * A rate's day scope spelled out for guests: "Monday – Friday",
 * "Saturday – Sunday", "Every day", "Mon, Wed, Fri".
 *
 * Deliberately wordier than describeBandDays, which the admin editor uses:
 * "Weekdays" is shorthand a club owner reads fluently next to a row of day
 * pills, but a guest comparing prices wants to be told which days those are.
 *
 * Contiguity is judged Monday-first, so a Saturday+Sunday rate reads as one
 * range rather than the two ends of a Sunday-first week.
 *
 * @param {object} rate - anything with a `days` array
 * @returns {string}
 */
export const describeRateDays = (rate) => {
  const days = Array.isArray(rate?.days)
    ? [...new Set(rate.days.map(Number))].filter(d => d >= 0 && d <= 6).sort((a, b) => a - b)
    : [];
  if (days.length === 0 || days.length === 7) return 'Every day';
  if (days.length === 1) return WEEKDAYS[days[0]].label;

  const monFirst = days.map(d => (d + 6) % 7).sort((a, b) => a - b);
  const present = new Set(monFirst);
  const nameOf = (m) => WEEKDAYS[(m + 1) % 7];

  // The run starts on the day whose predecessor is missing. Checked around
  // the week rather than left-to-right so Sat+Sun+Mon still reads as one
  // range instead of "Mon, Sat, Sun".
  const start = monFirst.find(day => !present.has((day + 6) % 7));
  if (start !== undefined) {
    const run = Array.from({ length: monFirst.length }, (_, i) => (start + i) % 7);
    if (run.every(day => present.has(day))) {
      return `${nameOf(start).label} – ${nameOf(run[run.length - 1]).label}`;
    }
  }
  return monFirst.map(m => nameOf(m).short).join(', ');
};

/** "₱1,500" or "₱1,500 – ₱2,000" for a choice's price span; '' when unpriced. */
export const formatPackagePriceRange = (minPrice, maxPrice, config = null) => {
  if (minPrice === null || minPrice === undefined) return '';
  const low = formatMoney(minPrice, config);
  if (maxPrice === null || maxPrice === undefined || Number(maxPrice) === Number(minPrice)) return low;
  return `${low} – ${formatMoney(maxPrice, config)}`;
};

/** "Birthday Event Packages" — a package's name as a screen heading. */
export const getPackageHeading = (pkg) => {
  const name = (pkg?.name || '').trim() || 'Package';
  return /packages?$/i.test(name) ? name : `${name} Packages`;
};

/** Distinct durations this package sells, ascending. */
export const getPackageDurations = (pkg) => [
  ...new Set((Array.isArray(pkg?.options) ? pkg.options : [])
    .map(opt => Number(opt?.hours) || 0)
    .filter(h => h > 0)),
].sort((a, b) => a - b);

/** Courts a package takes over, as numbers. */
export const getPackageCourts = (pkg) =>
  (Array.isArray(pkg?.courts) ? pkg.courts : []).map(Number).filter(Number.isFinite);

/** "3:00 PM" for an absolute hour that may run past 24 (a wrapping window). */
export const formatPackageHour = (hour) => {
  const h = ((Number(hour) || 0) % 24 + 24) % 24;
  const suffix = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
};

/** "3:00 PM – 12:00 AM (9 hrs)" for a package's bookable window. */
export const describePackageWindow = (pkg) => {
  const hours = getPackageWindowHours(pkg);
  return `${formatPackageHour(pkg?.startHour)} – ${formatPackageHour(pkg?.endHour)} (${hours} hr${hours === 1 ? '' : 's'})`;
};


// ---- Pricing ----
// A package is priced one of two ways, checked in this order:
//
//   1. A FIXED OPTION — "4 hours: P1,500 weekdays / P2,000 weekends". The
//      guest picks a preset duration and pays the flat price of whichever of
//      that option's rates covers their weekday.
//   2. HOURLY RATE BANDS — "6AM-3PM P1,000/hr, 3PM-5AM P1,200/hr". The guest
//      picks any start and end inside the window and pays per hour, with the
//      rate varying by time of day and weekday.
//
// Both use the same day-scoping convention as a sport's rate bands
// (`days: []` = every day), and a package may define both: the options are
// the shortcuts, the bands the fallback for any other duration.

/** @returns {Array} a package's own hourly rate bands. */
export const getPackageRateBands = (pkg) => (Array.isArray(pkg?.rateBands) ? pkg.rateBands : []);

/** True when this package can price an arbitrary start/end, not just presets. */
export const packageAllowsCustomDuration = (pkg) => getPackageRateBands(pkg).length > 0;

/**
 * True when the guest picks a start AND an end and pays by the hour, rather
 * than choosing from preset durations.
 *
 * A package sells one way or the other: presets take precedence wherever both
 * are configured (see getSelectablePackageDurations), so "hourly" means there
 * are no presets at all and there are bands to price with.
 */
export const packageIsHourlyPriced = (pkg) =>
  getMergedPackageOptions(pkg).length === 0 && packageAllowsCustomDuration(pkg);

/** "6:00 AM – 3:00 PM", or "All hours" for a band covering the whole day. */
export const describePackageBandHours = (band) => {
  const start = Number(band?.startHour);
  const end = Number(band?.endHour);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start === end) return 'All hours';
  return `${formatPackageHour(start)} – ${formatPackageHour(end)}`;
};

/**
 * The rate charged for each hour of a booking, in order.
 *
 * @returns {Array<number|null>} null in any position no band covers
 */
export const getPackageHourlyRates = (pkg, startHour, hours, weekday = null) =>
  Array.from(
    { length: Math.max(0, Number(hours) || 0) },
    (_, i) => getPackageHourlyRate(pkg, (Number(startHour) || 0) + i, weekday),
  );

/**
 * How an hourly booking's price is made up: "₱1,000/hr" when one rate covers
 * the whole run, "₱1,000 + ₱1,200" when it straddles a rate change. Empty
 * when any hour has no rate, which is a refusal rather than a total.
 */
export const describePackageHourlyCharge = (pkg, startHour, hours, weekday = null, config = null) => {
  const rates = getPackageHourlyRates(pkg, startHour, hours, weekday);
  if (rates.length === 0 || rates.some(rate => rate === null)) return '';
  const distinct = [...new Set(rates)];
  if (distinct.length === 1) return `${formatMoney(distinct[0], config)}/hr`;
  return rates.map(rate => formatMoney(rate, config)).join(' + ');
};

/**
 * The P/hour rate covering a given hour on a given weekday, or null.
 *
 * Bands wrap past midnight exactly like a sport's (isHourInBand), so an
 * evening band written 17 -> 5 covers 11PM. `hour` may be >= 24 for the small
 * hours of a wrapping window; it is folded back into 0-23 first.
 */
export const getPackageHourlyRate = (pkg, hour, weekday = null) => {
  const h = ((Number(hour) || 0) % 24 + 24) % 24;
  const band = getPackageRateBands(pkg)
    .filter(b => bandAppliesOnDay(b, weekday))
    .find(b => isHourInBand(b, h));
  return band ? (Number(band.price) || 0) : null;
};

/**
 * What a package costs for `hours` starting at `startHour` on `weekday`.
 *
 * A fixed option for exactly that duration wins; otherwise the hourly bands
 * are summed hour by hour, so a booking that straddles a rate change is
 * charged correctly on both sides of it. Returns null when the package can
 * price neither — which validation surfaces to the admin rather than letting
 * a guest reach checkout with a P0 total.
 *
 * `weekday` is the weekday of the START date and is applied to every hour,
 * including any that run past midnight: a club advertising a "Saturday rate"
 * means the night that begins on Saturday, not the small hours that
 * technically fall on Sunday.
 *
 * @returns {number|null}
 */
export const getPackagePrice = (pkg, startHour, hours, weekday = null) => {
  const duration = Number(hours) || 0;
  if (duration <= 0) return null;

  const fixed = getPackageOptionPrice(pkg, duration, weekday);
  if (fixed !== null) return fixed;

  if (!packageAllowsCustomDuration(pkg)) return null;
  let total = 0;
  for (let i = 0; i < duration; i += 1) {
    const rate = getPackageHourlyRate(pkg, (Number(startHour) || 0) + i, weekday);
    // A gap in the bands would silently undercharge; refuse instead.
    if (rate === null) return null;
    total += rate;
  }
  return total;
};

/**
 * Durations a guest may choose: the fixed options for that weekday, or —
 * when the package prices hourly — every length that fits the window.
 *
 * @returns {number[]} ascending
 */
export const getSelectablePackageDurations = (pkg, weekday = null) => {
  const fixed = getPackageOptionsForDay(pkg, weekday)
    .map(o => Number(o.hours) || 0)
    .filter(h => h > 0);
  if (fixed.length > 0) return [...new Set(fixed)].sort((a, b) => a - b);
  if (!packageAllowsCustomDuration(pkg)) return [];
  const max = getPackageWindowHours(pkg);
  return Array.from({ length: max }, (_, i) => i + 1);
};

// ---- Mapping a package onto the hourly slot grid ----
// Package bookings are stored as ordinary bookings — the same
// `timeSlots: { label: [courts] }` shape — so slot locking, the guest grid,
// the admin grid, the admin list and the export all keep working untouched.
// That only holds if a package's hours resolve to the SAME slot label
// strings the club configured; a generated label like "15-16" would lock a
// cell nothing else can see.

/**
 * The configured slot label covering an hour, or null when the club has no
 * slot for it (e.g. a package window running later than the club's day).
 *
 * @param {object} config
 * @param {number} hour - may be >= 24; folded into 0-23
 * @returns {string|null}
 */
export const getSlotLabelForHour = (config, hour) => {
  const h = ((Number(hour) || 0) % 24 + 24) % 24;
  // Searched across EVERY configured hour rather than the default list: the
  // label for a given hour is the same string whichever day it appears on,
  // and a package running into an hour that only exists at weekends still
  // needs to resolve to that same label.
  return getAllConfiguredTimeSlots(config).find(slot => parseTimeSlotStartHour(slot) === h) || null;
};

/**
 * How many whole hours of grid a cleanup allowance occupies. Rounded UP: a
 * 30-minute gap still makes the next hour unsellable, since the grid has no
 * finer unit than an hour.
 */
export const getPackageBufferHours = (config, pkg) =>
  Math.ceil(getPackageBufferMinutes(config, pkg) / 60);

/**
 * Every hour a package booking occupies, split into the hours the guest is
 * actually paying for and the cleanup hours held after them.
 *
 * The buffer is applied AFTER the event only — an event ending at 7PM with a
 * 60-minute allowance holds 7-8PM, and the next package may start at 8PM.
 *
 * @returns {{ eventHours: number[], bufferHours: number[], allHours: number[] }}
 */
export const getPackageOccupiedHours = (config, pkg, startHour, hours) => {
  const start = Number(startHour) || 0;
  const duration = Number(hours) || 0;
  const eventHours = Array.from({ length: Math.max(0, duration) }, (_, i) => start + i);
  const bufferHours = Array.from(
    { length: getPackageBufferHours(config, pkg) },
    (_, i) => start + duration + i,
  );
  return { eventHours, bufferHours, allHours: [...eventHours, ...bufferHours] };
};

// ---- Reading a saved package booking ----
// A package booking is stored as an ordinary booking record with a handful of
// `package*` fields on it (see createPendingBooking in App.jsx). These read
// that record back, so every screen showing a booking agrees on whether it
// was a package and what it cost.

/** True when a saved booking record came through the package flow. */
export const isPackageBooking = (bookingData) =>
  bookingData?.bookingSource === BOOKING_SOURCE_PACKAGE;

/**
 * What the package itself cost on a saved booking.
 *
 * Prefers the price stored at booking time. Records written before that field
 * existed are backed out of the total instead — the fee and any equipment are
 * the only other things in it — so an old confirmation still shows the right
 * number rather than the grand total on the package line.
 *
 * @param {object} bookingData
 * @returns {number}
 */
export const getPackageBookingPrice = (bookingData) => {
  const stored = Number(bookingData?.packagePrice);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const total = Number(bookingData?.paymentAmount) || 0;
  const fee = Number(bookingData?.bookingFee) || 0;
  const equipment = Number(bookingData?.equipmentRental?.total) || 0;
  return Math.max(0, total - fee - equipment);
};

/**
 * The courts a package booking covers, as raw numbers.
 *
 * Taken from `packageEventSlots` — the hours actually sold — rather than from
 * every key of `timeSlots`, which also holds the cleanup slots the club is
 * blocking out and the guest is not paying for.
 *
 * @param {object} bookingData
 * @returns {number[]}
 */
export const getPackageBookingCourts = (bookingData) => {
  const slots = bookingData?.packageEventSlots || Object.keys(bookingData?.timeSlots || {});
  return [...new Set(
    slots.flatMap(slot => (bookingData?.timeSlots || {})[slot] || []),
  )].map(Number).filter(Number.isFinite);
};

/** "3:00 PM – 7:00 PM · 4 hrs" for a saved package booking. */
export const describePackageBookingTime = (bookingData) => {
  const start = Number(bookingData?.packageStartHour) || 0;
  const hours = Number(bookingData?.packageHours) || 0;
  return `${formatPackageHour(start)} – ${formatPackageHour(start + hours)} · ${hours} hr${hours === 1 ? '' : 's'}`;
};

/** Non-empty inclusion lines, trimmed. */
export const getPackageInclusions = (pkg) =>
  (Array.isArray(pkg?.inclusions) ? pkg.inclusions : [])
    .map(inc => (typeof inc === 'string' ? inc : inc?.text) || '')
    .map(text => text.trim())
    .filter(Boolean);

/** Re-exported so package screens don't need a second import for the day pills. */
export { WEEKDAYS };
